"use client"

import { useEffect, useRef } from "react"

/**
 * Lets a horizontally-scrolling row (e.g. the mobile category strip) be scrolled
 * with a mouse — click-and-drag to pan, and vertical wheel maps to horizontal
 * scroll — on top of the native touch swipe. A drag past a small threshold
 * suppresses the click that would otherwise fire on the button under the
 * pointer, so panning never accidentally selects a category.
 *
 * Returns a ref to attach to the scroll container.
 */
export function useHorizontalDragScroll<T extends HTMLElement>() {
  const ref = useRef<T>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    let dragging = false
    let moved = false
    let startX = 0
    let startScrollLeft = 0

    const canScroll = () => el.scrollWidth > el.clientWidth + 1

    const onPointerDown = (e: PointerEvent) => {
      // Mouse only — touch keeps its native momentum scrolling.
      if (e.pointerType !== "mouse" || !canScroll()) return
      dragging = true
      moved = false
      startX = e.clientX
      startScrollLeft = el.scrollLeft
    }

    const onPointerMove = (e: PointerEvent) => {
      if (!dragging) return
      const dx = e.clientX - startX
      if (Math.abs(dx) > 4) moved = true
      el.scrollLeft = startScrollLeft - dx
    }

    const endDrag = () => {
      dragging = false
    }

    // Swallow the click that follows a real drag so it doesn't select a chip.
    const onClickCapture = (e: MouseEvent) => {
      if (moved) {
        e.preventDefault()
        e.stopPropagation()
        moved = false
      }
    }

    const onWheel = (e: WheelEvent) => {
      if (!canScroll()) return
      // Only translate a dominantly-vertical wheel; leave trackpad horizontal
      // gestures to the browser.
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        el.scrollLeft += e.deltaY
        e.preventDefault()
      }
    }

    el.addEventListener("pointerdown", onPointerDown)
    window.addEventListener("pointermove", onPointerMove)
    window.addEventListener("pointerup", endDrag)
    el.addEventListener("click", onClickCapture, true)
    el.addEventListener("wheel", onWheel, { passive: false })

    return () => {
      el.removeEventListener("pointerdown", onPointerDown)
      window.removeEventListener("pointermove", onPointerMove)
      window.removeEventListener("pointerup", endDrag)
      el.removeEventListener("click", onClickCapture, true)
      el.removeEventListener("wheel", onWheel)
    }
  }, [])

  return ref
}
